import {
  PDFDocument,
  StandardFonts,
  rgb,
  type PDFFont,
  type PDFImage,
  type PDFPage,
} from 'pdf-lib';
import {
  readDocxLayout,
  type DocxAlign,
  type DocxMargins,
  type DocxParagraph,
  type DocxRun,
} from '@/lib/patientReports/docxLayout';
import {
  prepareLetterhead,
  PREPRINTED_BOTTOM_PT,
  PREPRINTED_TOP_PT,
  type PatientReportPdfLetterhead,
} from '@/lib/patientReports/pdf';
import { toPdfWinAnsi } from '@/lib/pdf/winAnsi';

/**
 * Paint a filled .docx into an A4 PDF.
 * The web image is nginx and has no LibreOffice, so the preview uses the
 * Word parts directly: header, footer, body paragraphs, and raster images.
 * A page-sized PDF letterhead is copied behind every report page, so its
 * logo keeps its resolution. A small logo sits above the Word header.
 * Page tokens become the page index of these report pages.
 */

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MAX_PAGES = 40;

type Fonts = { body: PDFFont; bold: PDFFont; italic: PDFFont; boldItalic: PDFFont };

type Span = { text: string; bold: boolean; italic: boolean; size: number };

function pickFont(fonts: Fonts, span: { bold: boolean; italic: boolean }): PDFFont {
  if (span.bold && span.italic) return fonts.boldItalic;
  if (span.italic) return fonts.italic;
  if (span.bold) return fonts.bold;
  return fonts.body;
}

type TocSlot = {
  title: string;
  page: number | null;
  destPage: number | null;
  destTop: number | null;
  source: { page: PDFPage; yTop: number } | null;
};

type PaintLine = {
  height: number;
  gapAfter: number;
  pageBreak?: boolean;
  /** Text of this visual line, used to find where a topic starts. */
  lineText?: string;
  /** Text of the paragraph piece before wrapping. */
  paragraphText?: string;
  /** Set on a contents-list row. The page number is filled in after pagination. */
  tocTitle?: string;
  tocSlot?: TocSlot;
  /** Contents rows that jump to this line. */
  tocDestinations?: TocSlot[];
  draw: (page: PDFPage, yTop: number) => void;
};

export async function renderFilledDocxPdf(
  docx: Uint8Array,
  letterhead: PatientReportPdfLetterhead,
): Promise<Uint8Array> {
  const layout = readDocxLayout(docx);
  const pdf = await PDFDocument.create();
  const fonts: Fonts = {
    body: await pdf.embedFont(StandardFonts.Helvetica),
    bold: await pdf.embedFont(StandardFonts.HelveticaBold),
    italic: await pdf.embedFont(StandardFonts.HelveticaOblique),
    boldItalic: await pdf.embedFont(StandardFonts.HelveticaBoldOblique),
  };
  const painter = await prepareLetterhead(pdf, fonts.body, letterhead);
  const margin = layout.margin;
  const headerSample = await paragraphLines(pdf, layout.header, fonts, margin, 1, 1);
  const footerSample = await paragraphLines(pdf, layout.footer, fonts, margin, 1, 1);
  const bodyLines = await paragraphLines(pdf, layout.body, fonts, margin, 1, 1);
  const letterheadHeight = Math.min(painter.height, 220);
  const headerHeight = blockHeight(headerSample);
  const footerHeight = blockHeight(footerSample);
  const bands = pageBands(margin, letterheadHeight, headerHeight, footerHeight);
  const contentHeight = Math.max(120, bands.contentTop - bands.contentBottom);
  const pages = paginate(bodyLines, contentHeight);
  assignTocPages(pages);
  const total = pages.length;

  for (let index = 0; index < total; index += 1) {
    const page = painter.openPage ? painter.openPage() : pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    if (!painter.openPage) painter.draw(page, bands.letterheadStart);
    let y = bands.headerStart;
    const headerLines = await paragraphLines(pdf, layout.header, fonts, margin, index + 1, total);
    for (const line of headerLines) {
      if (!line.pageBreak) line.draw(page, y);
      y -= line.height + line.gapAfter;
    }
    y = bands.contentTop;
    for (const line of pages[index] ?? []) {
      if (!line.pageBreak) {
        for (const slot of line.tocDestinations ?? []) slot.destTop = y;
        line.draw(page, y);
      }
      y -= line.height + line.gapAfter;
    }
    let footerY = bands.contentBottom;
    const footerLines = await paragraphLines(pdf, layout.footer, fonts, margin, index + 1, total);
    for (const line of footerLines) {
      if (!line.pageBreak) line.draw(page, footerY);
      footerY -= line.height + line.gapAfter;
    }
  }

  for (const lines of pages) {
    for (const line of lines) {
      const slot = line.tocSlot;
      if (!slot?.source || slot.destPage == null || slot.destTop == null) continue;
      const dest = pdf.getPage(slot.destPage);
      addTocLink(pdf, slot.source.page, slot.source.yTop, line.height, margin, dest, slot.destTop);
    }
  }

  return pdf.save();
}

function blockHeight(lines: PaintLine[]): number {
  return lines.reduce((sum, line) => sum + line.height + line.gapAfter, 0);
}

/**
 * Top and bottom bands stay empty for a logo or address already printed
 * on the paper. A Word margin that is larger is kept. A drawn letterhead
 * pushes the text down when it needs more room than that band.
 */
function pageBands(
  margin: DocxMargins,
  letterheadHeight: number,
  headerHeight: number,
  footerHeight: number,
): { letterheadStart: number; headerStart: number; contentTop: number; contentBottom: number } {
  const topClear = Math.max(margin.topPt, PREPRINTED_TOP_PT);
  const bottomClear = Math.max(margin.bottomPt, PREPRINTED_BOTTOM_PT);
  const letterheadStart = PAGE_HEIGHT - Math.max(margin.headerPt, 28);
  const headerStart = Math.min(letterheadStart - letterheadHeight, PAGE_HEIGHT - topClear);
  return {
    letterheadStart,
    headerStart,
    contentTop: headerStart - headerHeight,
    contentBottom: bottomClear + footerHeight,
  };
}

function paginate(lines: PaintLine[], contentHeight: number): PaintLine[][] {
  const pages: PaintLine[][] = [];
  let bucket: PaintLine[] = [];
  let used = 0;
  const push = () => {
    if (pages.length >= MAX_PAGES) return;
    pages.push(bucket);
    bucket = [];
    used = 0;
  };
  for (const line of lines) {
    if (pages.length >= MAX_PAGES) break;
    if (line.pageBreak) {
      if (bucket.length > 0) push();
      continue;
    }
    const need = line.height + line.gapAfter;
    if (bucket.length > 0 && used + need > contentHeight) push();
    if (pages.length >= MAX_PAGES) break;
    bucket.push(line);
    used += need;
  }
  if (bucket.length > 0 && pages.length < MAX_PAGES) pages.push(bucket);
  return pages.length > 0 ? pages : [[]];
}

async function paragraphLines(
  pdf: PDFDocument,
  paragraphs: DocxParagraph[],
  fonts: Fonts,
  margin: DocxMargins,
  pageNo: number,
  pageCount: number,
): Promise<PaintLine[]> {
  const lines: PaintLine[] = [];
  for (const paragraph of paragraphs) {
    if (paragraph.beforePt > 0) lines.push(spacer(paragraph.beforePt));
    const built = await linesOfParagraph(pdf, paragraph, fonts, margin, pageNo, pageCount);
    // An empty paragraph is a blank line in Word. Dropping it collapsed the
    // gap the template put between two blocks of content.
    const painted = built.length > 0 ? built : [spacer(lineBox(paragraph.fontPt, paragraph))];
    const last = painted.length - 1;
    const tail = painted[last];
    if (tail) {
      painted[last] = { ...tail, gapAfter: tail.gapAfter + paragraph.afterPt };
    }
    lines.push(...painted);
  }
  return lines;
}

function spacer(height: number): PaintLine {
  return { height, gapAfter: 0, draw: () => undefined };
}

async function linesOfParagraph(
  pdf: PDFDocument,
  paragraph: DocxParagraph,
  fonts: Fonts,
  margin: DocxMargins,
  pageNo: number,
  pageCount: number,
): Promise<PaintLine[]> {
  const contentWidth = PAGE_WIDTH - margin.leftPt - margin.rightPt;
  const expanded = expandRuns(paragraph.runs, pageNo, pageCount);
  const tocAt = expanded.findIndex((run) => run.kind === 'tocPage');
  const tabAt = expanded.findIndex((run) => run.kind === 'tab');
  if (tocAt >= 0) {
    const left = spans(expanded.slice(0, tabAt >= 0 && tabAt < tocAt ? tabAt : tocAt));
    if (left.length === 0) return [];
    const marker = expanded[tocAt];
    const linkTitle = marker?.kind === 'tocPage' ? (marker.title ?? '') : '';
    return [tocPaintLine(left, fonts, margin, paragraph, linkTitle)];
  }
  const simple =
    tabAt >= 0 &&
    expanded.every((run) => run.kind === 'text' || run.kind === 'tab' || run.kind === 'field');
  if (simple) {
    const left = spans(expanded.slice(0, tabAt));
    const right = spans(expanded.slice(tabAt + 1));
    if (left.length === 0 && right.length === 0) return [];
    const tab = paragraph.tabs.find((item) => item.val === 'right') ?? paragraph.tabs[0];
    return [tabLine(left, right, fonts, margin, tab?.posPt ?? contentWidth, paragraph)];
  }

  const lines: PaintLine[] = [];
  let buffer: Span[] = [];
  let endedOnBreak = false;
  let lineSize = paragraph.fontPt;
  const flush = () => {
    if (buffer.length === 0) return;
    const source = buffer.map((span) => span.text).join('');
    wrapSpans(buffer, contentWidth, fonts).forEach((row, index) => {
      const line = textLine(row, fonts, margin, paragraph.align, paragraph);
      line.lineText = row.map((span) => span.text).join('');
      if (index === 0) line.paragraphText = source.trim();
      lines.push(line);
    });
    buffer = [];
  };
  const blankLine = () => {
    lines.push(spacer(lineBox(lineSize, paragraph)));
  };
  for (const run of expanded) {
    if (run.kind === 'page') {
      flush();
      lines.push({ height: 0, gapAfter: 0, pageBreak: true, draw: () => undefined });
      endedOnBreak = false;
      continue;
    }
    if (run.kind === 'tab') {
      flush();
      endedOnBreak = false;
      continue;
    }
    if (run.kind === 'break') {
      // A break ends the current line. A second break is the blank line
      // between two pieces of content.
      if (buffer.length > 0) flush();
      else blankLine();
      endedOnBreak = true;
      continue;
    }
    if (run.kind === 'image') {
      flush();
      endedOnBreak = false;
      const painted = await imageLine(pdf, run.bytes, run.widthPt, run.heightPt, margin);
      if (painted) lines.push(painted);
      continue;
    }
    if (run.kind === 'text' && run.text) {
      lineSize = Math.max(lineSize, run.sizePt);
      buffer.push({ text: run.text, bold: run.bold, italic: run.italic, size: run.sizePt });
      endedOnBreak = false;
    }
  }
  flush();
  if (endedOnBreak) blankLine();
  return lines;
}

function expandRuns(runs: DocxRun[], pageNo: number, pageCount: number): DocxRun[] {
  return runs.map((run) => {
    if (run.kind !== 'field') return run;
    const text = run.instruction === 'PAGE' ? String(pageNo) : String(pageCount);
    return { kind: 'text', text, bold: run.bold, italic: run.italic, sizePt: run.sizePt };
  });
}

function spans(runs: DocxRun[]): Span[] {
  return runs.flatMap((run) =>
    run.kind === 'text' && run.text
      ? [{ text: run.text, bold: run.bold, italic: run.italic, size: run.sizePt }]
      : [],
  );
}

function assignTocPages(pages: PaintLine[][]): void {
  let cursor = { page: 0, line: -1 };
  for (let page = 0; page < pages.length; page += 1) {
    const lines = pages[page] ?? [];
    for (let line = 0; line < lines.length; line += 1) {
      const item = lines[line];
      if (!item?.tocSlot || !item.tocTitle) continue;
      const start = laterPosition(cursor, { page, line });
      const found = findHeadingAfter(pages, item.tocTitle, start);
      if (!found) continue;
      item.tocSlot.page = found.number;
      item.tocSlot.destPage = found.page;
      const destLine = pages[found.page]?.[found.line];
      if (destLine) {
        destLine.tocDestinations = destLine.tocDestinations ?? [];
        destLine.tocDestinations.push(item.tocSlot);
      }
      cursor = { page: found.page, line: found.line };
    }
  }
}

function laterPosition(
  a: { page: number; line: number },
  b: { page: number; line: number },
): { page: number; line: number } {
  if (a.page !== b.page) return a.page > b.page ? a : b;
  return a.line > b.line ? a : b;
}

function findHeadingAfter(
  pages: PaintLine[][],
  title: string,
  after: { page: number; line: number },
): { page: number; line: number; number: number } | null {
  const forward = scanHeading(pages, title, after, null);
  if (forward) return forward;
  if (after.page === 0 && after.line < 0) return null;
  return scanHeading(pages, title, { page: 0, line: -1 }, after);
}

function scanHeading(
  pages: PaintLine[][],
  title: string,
  after: { page: number; line: number },
  stop: { page: number; line: number } | null,
): { page: number; line: number; number: number } | null {
  let seenCursor = after.page === 0 && after.line < 0;
  for (let page = 0; page < pages.length; page += 1) {
    const lines = pages[page] ?? [];
    for (let line = 0; line < lines.length; line += 1) {
      if (stop && page === stop.page && line === stop.line) return null;
      if (!seenCursor) {
        if (page === after.page && line === after.line) seenCursor = true;
        continue;
      }
      const item = lines[line];
      if (!item || item.tocTitle) continue;
      const text = (item.paragraphText ?? item.lineText ?? '').trim();
      if (text === title) return { page, line, number: page + 1 };
    }
  }
  return null;
}

function tocPaintLine(
  left: Span[],
  fonts: Fonts,
  margin: DocxMargins,
  paragraph: DocxParagraph,
  linkTitle: string,
): PaintLine {
  const visible = left
    .map((span) => span.text)
    .join('')
    .trim();
  const title = linkTitle.trim() || visible;
  const slot: TocSlot = { title, page: null, destPage: null, destTop: null, source: null };
  const size = Math.max(11, ...left.map((span) => span.size));
  const height = lineBox(size, paragraph);
  return {
    height,
    gapAfter: 0,
    tocTitle: title,
    tocSlot: slot,
    draw: (page, yTop) => {
      slot.source = { page, yTop };
      const baseline = yTop - size;
      const leftEnd = drawSpans(page, left, margin.leftPt, baseline, fonts);
      const number = slot.page == null ? '' : String(slot.page);
      if (!number) return;
      const numberWidth = fonts.body.widthOfTextAtSize(number, size);
      const rightEdge = PAGE_WIDTH - margin.rightPt;
      const numberX = rightEdge - numberWidth;
      const dotWidth = fonts.body.widthOfTextAtSize('.', size);
      let dotX = leftEnd + size * 0.6;
      const ink = rgb(0.12, 0.14, 0.16);
      while (dotX + dotWidth < numberX - 4) {
        page.drawText('.', { x: dotX, y: baseline, size, font: fonts.body, color: ink });
        dotX += dotWidth * 2;
      }
      page.drawText(number, {
        x: numberX,
        y: baseline,
        size,
        font: fonts.body,
        color: ink,
      });
    },
  };
}

function addTocLink(
  pdf: PDFDocument,
  source: PDFPage,
  yTop: number,
  height: number,
  margin: DocxMargins,
  dest: PDFPage,
  destTop: number,
): void {
  const annot = pdf.context.register(
    pdf.context.obj({
      Type: 'Annot',
      Subtype: 'Link',
      Rect: [margin.leftPt, yTop - height, PAGE_WIDTH - margin.rightPt, yTop],
      Border: [0, 0, 0],
      A: {
        S: 'GoTo',
        D: [dest.ref, 'XYZ', null, destTop, null],
      },
    }),
  );
  source.node.addAnnot(annot);
}

function tabLine(
  left: Span[],
  right: Span[],
  fonts: Fonts,
  margin: DocxMargins,
  tabPosPt: number,
  paragraph: DocxParagraph,
): PaintLine {
  const size = Math.max(11, ...[...left, ...right].map((span) => span.size));
  const height = lineBox(size, paragraph);
  return {
    height,
    gapAfter: 0,
    draw: (page, yTop) => {
      const baseline = yTop - size;
      const leftEnd = drawSpans(page, left, margin.leftPt, baseline, fonts);
      const rightWidth = measure(right, fonts);
      const rightEdge = Math.min(
        PAGE_WIDTH - margin.rightPt,
        margin.leftPt + Math.max(tabPosPt, 0),
      );
      const x = Math.max(leftEnd + 8, rightEdge - rightWidth);
      drawSpans(page, right, x, baseline, fonts);
    },
  };
}

function textLine(
  row: Span[],
  fonts: Fonts,
  margin: DocxMargins,
  align: DocxAlign,
  paragraph: DocxParagraph,
): PaintLine {
  const size = Math.max(11, ...row.map((span) => span.size));
  const contentWidth = PAGE_WIDTH - margin.leftPt - margin.rightPt;
  return {
    height: lineBox(size, paragraph),
    gapAfter: 0,
    draw: (page, yTop) => {
      const width = measure(row, fonts);
      let x = margin.leftPt;
      if (align === 'center') x = margin.leftPt + Math.max(0, (contentWidth - width) / 2);
      if (align === 'right') x = PAGE_WIDTH - margin.rightPt - width;
      drawSpans(page, row, x, yTop - size, fonts);
    },
  };
}

async function imageLine(
  pdf: PDFDocument,
  bytes: Uint8Array,
  widthPt: number,
  heightPt: number,
  margin: DocxMargins,
): Promise<PaintLine | null> {
  let image: PDFImage;
  try {
    const png = bytes.length > 8 && bytes[0] === 0x89 && bytes[1] === 0x50;
    image = png ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes);
  } catch {
    return null;
  }
  const contentWidth = PAGE_WIDTH - margin.leftPt - margin.rightPt;
  let width = widthPt > 0 ? widthPt : image.width;
  let height = heightPt > 0 ? heightPt : image.height;
  if (width > contentWidth) {
    height *= contentWidth / width;
    width = contentWidth;
  }
  const square = width > 0 && height > 0 && Math.abs(width / height - 1) < 0.2;
  const cap = square ? 320 : 140;
  if (height > cap) {
    width *= cap / height;
    height = cap;
  }
  return {
    height,
    gapAfter: 4,
    draw: (page, yTop) => {
      page.drawImage(image, {
        x: margin.leftPt,
        y: yTop - height,
        width,
        height,
      });
    },
  };
}

function lineBox(size: number, paragraph: DocxParagraph): number {
  const line = paragraph.lineTwips;
  if (!line || line <= 0) return size + 3;
  if (paragraph.lineRule === 'exact') return Math.max(1, line / 20);
  if (paragraph.lineRule === 'atLeast') return Math.max(size, line / 20);
  return Math.max(size, size * (line / 240));
}

function wrapSpans(spans: Span[], maxWidth: number, fonts: Fonts): Span[][] {
  const lines: Span[][] = [];
  let current: Span[] = [];
  let width = 0;
  const commit = () => {
    const trimmed = trimSpans(current);
    if (trimmed.length > 0) lines.push(trimmed);
    current = [];
    width = 0;
  };
  for (const span of spans) {
    for (const part of span.text.split(/(\s+)/)) {
      if (!part) continue;
      const painted = toPdfWinAnsi(part);
      const partWidth = pickFont(fonts, span).widthOfTextAtSize(painted || ' ', span.size);
      if (width + partWidth > maxWidth && current.length > 0 && !/^\s+$/.test(part)) commit();
      if (/^\s+$/.test(part) && current.length === 0) continue;
      current.push({ ...span, text: part });
      width += partWidth;
    }
  }
  commit();
  return lines;
}

function trimSpans(spans: Span[]): Span[] {
  const next = [...spans];
  while (next.length > 0 && /^\s+$/.test(next[next.length - 1]?.text ?? '')) next.pop();
  while (next.length > 0 && /^\s+$/.test(next[0]?.text ?? '')) next.shift();
  return next;
}

function measure(spans: Span[], fonts: Fonts): number {
  return spans.reduce((sum, span) => {
    const font = pickFont(fonts, span);
    return sum + font.widthOfTextAtSize(toPdfWinAnsi(span.text) || '', span.size);
  }, 0);
}

function drawSpans(
  page: PDFPage,
  spans: Span[],
  x: number,
  baseline: number,
  fonts: Fonts,
): number {
  let cursor = x;
  for (const span of spans) {
    const text = toPdfWinAnsi(span.text);
    if (!text) continue;
    const font = pickFont(fonts, span);
    page.drawText(text, {
      x: cursor,
      y: baseline,
      size: span.size,
      font,
      color: rgb(0.12, 0.14, 0.16),
    });
    cursor += font.widthOfTextAtSize(text, span.size);
  }
  return cursor;
}
